// Ladder movement logic v2 - 2026-02-11
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type AttendanceStatus = 'confirmed' | 'pending' | 'not_attending';

export interface CourtParticipant {
  playerName: string;
  attendanceStatus: AttendanceStatus;
  registrationId: string | null;
  userId: string | null;
  movementLabel?: string;
  movementType?: string;
  matchesPlayed?: boolean;
}

interface UseCourtParticipantsAttendanceResult {
  participants: CourtParticipant[];
  courtNumber: number | null;
  isLoading: boolean;
  error: Error | null;
}

export const useCourtParticipantsAttendance = (
  eventId: string,
  encounterNumber: number
): UseCourtParticipantsAttendanceResult => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['court-participants-attendance', eventId, encounterNumber],
    queryFn: async (): Promise<{ participants: CourtParticipant[]; courtNumber: number | null }> => {
      if (!eventId || !encounterNumber) {
        return { participants: [], courtNumber: null };
      }

      // Step 1: Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        console.error('Error getting user:', userError);
        return { participants: [], courtNumber: null };
      }

      // Step 2: Get user's registration(s) for this event (may have multiple)
      const { data: registrations, error: regError } = await supabase
        .from('registrations')
        .select('id')
        .eq('event_id', eventId)
        .eq('status', 'confirmed')
        .or(`user_id.eq.${user.id},partner_user_id.eq.${user.id}`);

      if (regError) {
        console.error('Error fetching registration:', regError);
        return { participants: [], courtNumber: null };
      }

      if (!registrations || registrations.length === 0) {
        return { participants: [], courtNumber: null };
      }

      const registrationIds = registrations.map(r => r.id);

      // Step 3: Find user's court for this encounter
      const orFilter = registrationIds.map(id => `team1_registration_id.eq.${id},team2_registration_id.eq.${id}`).join(',');
      const { data: userMatches, error: userMatchError } = await supabase
        .from('league_scheduled_matches')
        .select('court_number')
        .eq('event_id', eventId)
        .eq('encounter_number', encounterNumber)
        .or(orFilter)
        .limit(1);

      if (userMatchError) {
        console.error('Error finding user court:', userMatchError);
        return { participants: [], courtNumber: null };
      }

      if (!userMatches || userMatches.length === 0) {
        return { participants: [], courtNumber: null };
      }

      const courtNumber = userMatches[0].court_number;

      // Step 4: Get all matches ONLY for user's court in this encounter
      const { data: matches, error: matchesError } = await supabase
        .from('league_scheduled_matches')
        .select('team1_player1_name, team1_player2_name, team2_player1_name, team2_player2_name, team1_registration_id, team2_registration_id, court_number')
        .eq('event_id', eventId)
        .eq('encounter_number', encounterNumber)
        .eq('court_number', courtNumber);

      if (matchesError) {
        console.error('Error fetching matches:', matchesError);
        throw matchesError;
      }

      if (!matches || matches.length === 0) {
        return { participants: [], courtNumber };
      }

      // Extract unique player names from user's court
      const playerNames = new Set<string>();
      
      matches.forEach(match => {
        if (match.team1_player1_name) playerNames.add(match.team1_player1_name);
        if (match.team1_player2_name) playerNames.add(match.team1_player2_name);
        if (match.team2_player1_name) playerNames.add(match.team2_player1_name);
        if (match.team2_player2_name) playerNames.add(match.team2_player2_name);
      });

      // Step 5: Get each player's OWN registration to resolve their real user_id
      const playerNameArray = Array.from(playerNames);
      
      const playerInfoMap = new Map<string, { registrationId: string | null; userId: string | null }>();
      
      if (playerNameArray.length > 0) {
        const { data: regs, error: regErr } = await supabase
          .from('registrations')
          .select('id, user_id, player1_name')
          .eq('event_id', eventId)
          .eq('status', 'confirmed')
          .in('player1_name', playerNameArray);

        if (regErr) {
          console.error('Error fetching registrations by name:', regErr);
        } else if (regs) {
          regs.forEach(reg => {
            if (reg.player1_name) {
              playerInfoMap.set(reg.player1_name, { registrationId: reg.id, userId: reg.user_id });
            }
          });
        }
      }

      // Step 6: Get attendance status for all resolved user_ids
      const userIds = Array.from(playerInfoMap.values()).map(v => v.userId).filter(Boolean) as string[];
      
      let attendanceMap = new Map<string, AttendanceStatus>();
      
      if (userIds.length > 0) {
        const { data: attendance, error: attendanceError } = await supabase
          .from('league_attendance')
          .select('user_id, status')
          .eq('event_id', eventId)
          .eq('week_number', encounterNumber)
          .in('user_id', userIds);

        if (attendanceError) {
          console.error('Error fetching attendance:', attendanceError);
        } else if (attendance) {
          attendance.forEach(att => {
            const status = att.status as AttendanceStatus;
            attendanceMap.set(att.user_id, status === 'confirmed' || status === 'not_attending' ? status : 'pending');
          });
        }
      }

      // Step 7: Get ranking snapshots (before/after) to calculate movement
      let movementMap = new Map<string, { label: string; type: string }>();
      let matchesPlayed = false;

      // Fetch 'before' and 'after' snapshots for this encounter
      const { data: beforeSnapshots } = await supabase
        .from('league_ladder_rankings')
        .select('registration_id, position')
        .eq('event_id', eventId)
        .eq('encounter_number', encounterNumber)
        .eq('snapshot_type', 'before');

      const { data: afterSnapshots } = await supabase
        .from('league_ladder_rankings')
        .select('registration_id, position')
        .eq('event_id', eventId)
        .eq('encounter_number', encounterNumber)
        .eq('snapshot_type', 'after');

      if (afterSnapshots && afterSnapshots.length > 0 && beforeSnapshots && beforeSnapshots.length > 0) {
        matchesPlayed = true;
        const beforeMap = new Map<string, number>();
        beforeSnapshots.forEach(s => beforeMap.set(s.registration_id, s.position));

        afterSnapshots.forEach(s => {
          const beforePos = beforeMap.get(s.registration_id);
          if (beforePos !== undefined) {
            const diff = beforePos - s.position;
            if (diff > 0) {
              movementMap.set(s.registration_id, { label: `+${diff}`, type: 'up' });
            } else if (diff < 0) {
              movementMap.set(s.registration_id, { label: `${diff}`, type: 'down' });
            } else {
              movementMap.set(s.registration_id, { label: 'Mantiene', type: 'neutral' });
            }
          }
        });
      } else {
        // FALLBACK: Calculate movement from league_match_results when ladder rankings are empty
        const { data: matchResults } = await supabase
          .from('league_match_results')
          .select('team1_player1_name, team1_player2_name, team2_player1_name, team2_player2_name, team1_games_won, team1_games_lost, team2_games_won, team2_games_lost, court_number')
          .eq('event_id', eventId)
          .eq('week_number', encounterNumber);

        if (matchResults && matchResults.length > 0) {
          matchesPlayed = true;

          // Aggregate games won/lost per individual player across ALL courts
          const playerStats = new Map<string, { won: number; lost: number; court: number }>();

          matchResults.forEach(r => {
            const players = [
              { name: r.team1_player1_name, won: r.team1_games_won || 0, lost: r.team1_games_lost || 0, court: r.court_number || 1 },
              { name: r.team1_player2_name, won: r.team1_games_won || 0, lost: r.team1_games_lost || 0, court: r.court_number || 1 },
              { name: r.team2_player1_name, won: r.team2_games_won || 0, lost: r.team2_games_lost || 0, court: r.court_number || 1 },
              { name: r.team2_player2_name, won: r.team2_games_won || 0, lost: r.team2_games_lost || 0, court: r.court_number || 1 },
            ];
            players.forEach(p => {
              if (!p.name) return;
              const existing = playerStats.get(p.name) || { won: 0, lost: 0, court: p.court };
              existing.won += p.won;
              existing.lost += p.lost;
              existing.court = p.court;
              playerStats.set(p.name, existing);
            });
          });

          // Group players by court and sort by game difference within each court
          const courtGroups = new Map<number, { name: string; diff: number }[]>();
          playerStats.forEach((stats, name) => {
            const group = courtGroups.get(stats.court) || [];
            group.push({ name, diff: stats.won - stats.lost });
            courtGroups.set(stats.court, group);
          });

          // Sort courts ascending, players within court by diff descending
          const sortedCourts = Array.from(courtGroups.keys()).sort((a, b) => a - b);
          const minCourt = sortedCourts[0];
          const maxCourt = sortedCourts[sortedCourts.length - 1];

          // Ladder logic: best of each court (except court 1) goes up, worst (except last court) goes down
          sortedCourts.forEach(ct => {
            const group = courtGroups.get(ct)!;
            group.sort((a, b) => b.diff - a.diff);
            const bestPlayer = group[0];
            const worstPlayer = group[group.length - 1];

            group.forEach(p => {
              const info = playerInfoMap.get(p.name);
              const regId = info?.registrationId;
              if (!regId) return;

              if (p === bestPlayer && ct > minCourt) {
                movementMap.set(regId, { label: 'Sube', type: 'up' });
              } else if (p === worstPlayer && ct < maxCourt) {
                movementMap.set(regId, { label: 'Baja', type: 'down' });
              } else {
                movementMap.set(regId, { label: 'Mantiene', type: 'neutral' });
              }
            });
          });
        }
      }

      // Step 8: Build the participants array
      const participants: CourtParticipant[] = [];
      
      playerNames.forEach((playerName) => {
        const info = playerInfoMap.get(playerName);
        const registrationId = info?.registrationId || null;
        const userId = info?.userId || null;
        const attendanceStatus = userId ? (attendanceMap.get(userId) || 'pending') : 'pending';
        const movement = registrationId ? movementMap.get(registrationId) : undefined;
        
        participants.push({
          playerName,
          attendanceStatus,
          registrationId,
          userId,
          movementLabel: movement?.label,
          movementType: movement?.type,
          matchesPlayed
        });
      });

      return { participants, courtNumber };
    },
    enabled: !!eventId && !!encounterNumber,
    staleTime: 30000,
  });

  return {
    participants: data?.participants || [],
    courtNumber: data?.courtNumber || null,
    isLoading,
    error: error as Error | null
  };
};
